import { useEffect, useMemo, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import _debounce from "lodash/debounce";

const sessionKey = "scrollRestoration";
const sessionData = getSessionData();
const prevScrollPosYs = new Map(sessionData);
const prevScrollPosYsMax = 100;

export default function PageScrollRestoration() {
  const { hash, pathname, search } = useLocation();
  let history = useHistory();

  const activeLocation = useRef(`${pathname}${search}${hash}`);
  const originalScrollRestoration = useRef();

  const saveSessionData = useMemo(
    () => _debounce(storeSessionData, 1000, { leading: false, trailing: true }),
    []
  );

  const saveScrollYPos = useMemo(
    () =>
      _debounce(
        () => {
          // Delete oldest saved scroll position once max limit
          // is exceeded to prevent a memory leak
          if (
            !prevScrollPosYs.get(activeLocation.current) &&
            prevScrollPosYs.size >= prevScrollPosYsMax
          ) {
            const oldestScrollPosY = prevScrollPosYs.keys().next().value;
            prevScrollPosYs.delete(oldestScrollPosY);
          }
          prevScrollPosYs.set(activeLocation.current, getScrollPosY());
          saveSessionData();
        },
        100,
        { leading: false, trailing: true }
      ),
    [saveSessionData]
  );

  // Override browser's default behavior
  useEffect(() => {
    if (window.history.scrollRestoration) {
      originalScrollRestoration.current = window.history.scrollRestoration;
      window.history.scrollRestoration = "manual";
    }

    return function cleanUp() {
      if (originalScrollRestoration.current) {
        window.history.scrollRestoration = originalScrollRestoration.current;
      }
    };
  }, []);

  // Capture scroll-y positions
  useEffect(() => {
    window.addEventListener("scroll", saveScrollYPos, {
      passive: true,
    });
    window.addEventListener("touchmove", saveScrollYPos, {
      passive: true,
    });

    return function cleanUp() {
      window.removeEventListener("scroll", saveScrollYPos);
      window.removeEventListener("touchmove", saveScrollYPos);
    };
  }, [saveScrollYPos]);

  // Capture location key on location change
  useEffect(() => {
    const loc = `${pathname}${search}${hash}`;
    activeLocation.current = loc;

    // Scroll to saved scroll location if this is a back/forward event
    if (history.action === "POP") {
      const prevScrollPosY = prevScrollPosYs.get(loc) || 0;
      scrollToPosY(prevScrollPosY);
    }
  }, [hash, pathname, search]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

function getSessionData() {
  let sessionData;

  try {
    const unparsedData = sessionStorage.getItem(sessionKey);
    sessionData = JSON.parse(unparsedData);
  } catch (e) {}

  return sessionData;
}

function storeSessionData() {
  try {
    sessionStorage.setItem(sessionKey, JSON.stringify([...prevScrollPosYs]));
  } catch (e) {}
}

function getScrollPosY() {
  const pageWrapper = document.getElementById("page-wrapper");
  return !!pageWrapper ? pageWrapper.scrollTop : window.pageYOffset;
}

function scrollToPosY(posY = 0) {
  let attempts = 1;
  const delay = 150;

  setTimeout(() => {
    if (window.scrollY !== posY) {
      scrollMaybe();
    }
  }, delay);

  function scrollMaybe() {
    const pageWrapper = document.getElementById("page-wrapper");
    const pageHeight = !!pageWrapper
      ? pageWrapper.scrollHeight
      : document.body.scrollHeight;
    const scrollEl = !!pageWrapper ? pageWrapper : window;

    if (pageHeight >= posY) {
      scrollEl.scrollTo(0, posY);
    } else if (attempts < 10) {
      attempts++;
      setTimeout(scrollMaybe, delay);
    }
  }
}
